import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Ticket, Clock, CheckCircle2, X, Inbox, User as UserIcon, AlarmClock,
  RefreshCw, Trash2, Edit3, Hand, Undo2, Check, RotateCcw, Building2, MessageSquare,
  ShieldCheck, Save,
} from 'lucide-react';
import { BROKERS, BrokerPicker } from '../components/admin/adminHelpers';
import { useAuth } from '../contexts/AuthContext';

const token = () => sessionStorage.getItem('auth_token');
const api = (url, opts = {}) => fetch(url, {
  ...opts,
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
}).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

const STATUSES = {
  open:     { label: 'Open',     color: '#3d61a4', bg: '#eef2fa' },
  snoozed:  { label: 'Uitgezet', color: '#d97706', bg: '#fffbeb' },
  resolved: { label: 'Opgelost', color: '#16a34a', bg: '#f0fdf4' },
};
const PRIORITIES = {
  low:    { label: 'Laag',    color: '#7b859e' },
  normal: { label: 'Normaal', color: '#3d61a4' },
  high:   { label: 'Hoog',    color: '#d97706' },
  urgent: { label: 'Urgent',  color: '#dc2626' },
};
const CATEGORIES = ['onboarding', 'client', 'fx', 'trade', 'compliance', 'other'];

const daysBadge = (d) =>
  d > 7 ? { bg: '#fef2f2', color: '#dc2626' }
  : d > 3 ? { bg: '#fffbeb', color: '#d97706' }
  : { bg: '#f3f4f8', color: '#566079' };

const dateNL = (iso) => iso ? new Date(iso).toLocaleDateString('nl-NL') : '';
const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'zojuist';
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} dgn geleden`;
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })
    + ' ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};
const addDaysISO = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const addMonthISO = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return d.toISOString().slice(0, 10); };

const SNOOZE_PRESETS = [
  { label: 'Morgen',   value: () => addDaysISO(1) },
  { label: '3 dagen',  value: () => addDaysISO(3) },
  { label: '1 week',   value: () => addDaysISO(7) },
  { label: '2 weken',  value: () => addDaysISO(14) },
  { label: '1 maand',  value: () => addMonthISO() },
];

const EMPTY_FORM = { title: '', description: '', priority: 'normal', category: 'other', assigned_to: '', broker: null, related_lead_id: null, related_lead_name: '' };

export default function TicketsPage() {
  const { user } = useAuth();
  const [tabs, setTabs] = useState({ general_count: 0, my_count: 0, snoozed_count: 0, users: [] });
  const [assignees, setAssignees] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Klant zoeken in de create-modal
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [editingTicket, setEditingTicket] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [snoozeTicket, setSnoozeTicket] = useState(null);
  const [snoozeForm, setSnoozeForm] = useState({ until: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [detailTicket, setDetailTicket] = useState(null);
  const [detailEdit, setDetailEdit] = useState(null); // bewerkbare kopie (zoals caseEdit in ComplianceTab)
  const [detailTab, setDetailTab] = useState('details'); // 'details' | 'actions'
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [newAction, setNewAction] = useState('');
  const [postingAction, setPostingAction] = useState(false);

  const isAdmin = ['admin_pay', 'admin_trade'].includes(user?.role) || user?.is_teamleader;
  // Finance is read-only: geen actie-/opslaan-knoppen, alleen lezen (incl. actielog)
  const isFinance = user?.role === 'finance' && !user?.is_teamleader;

  const fetchTabs = useCallback(async () => {
    try {
      const data = await api('/api/v1/tickets/tabs');
      setTabs(data && typeof data === 'object' ? { users: [], ...data } : { general_count: 0, my_count: 0, snoozed_count: 0, users: [] });
    } catch (e) { console.error(e); }
  }, []);

  // Toewijsbare gebruikers (actief + ticketrol) — los van tabs.users (tab-balk)
  const fetchAssignees = useCallback(async () => {
    try {
      const data = await api('/api/v1/tickets/assignees');
      setAssignees(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tab', activeTab);
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      const data = await api(`/api/v1/tickets/?${params}`);
      const items = Array.isArray(data) ? data : (data?.tickets || []);
      setTickets(items);
      setTotal(Array.isArray(data) ? items.length : (data?.total ?? items.length));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeTab, filterStatus, filterPriority]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => { fetchTabs(); }, [fetchTabs]);
  useEffect(() => { fetchAssignees(); }, [fetchAssignees]);

  const refreshAll = useCallback(() => { fetchTickets(); fetchTabs(); }, [fetchTickets, fetchTabs]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setFilterStatus('');
    setFilterPriority('');
  };

  const fetchActions = useCallback(async (id) => {
    setActionsLoading(true);
    try {
      const data = await api(`/api/v1/tickets/${id}/actions`);
      setActions(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setActionsLoading(false); }
  }, []);

  const ticketToEdit = (t) => ({
    title: t.title || '',
    description: t.description || '',
    status: t.status || 'open',
    priority: t.priority || 'normal',
    category: t.category || 'other',
    broker: t.broker || null,
    assigned_to_id: t.assigned_to_id ? String(t.assigned_to_id) : '',
  });

  const openDetail = (t) => {
    setDetailTicket(t);
    setDetailEdit(ticketToEdit(t));
    setDetailTab('details');
    setNewAction('');
    setActions([]);
    fetchActions(t.id);
  };

  const closeDetail = () => { setDetailTicket(null); setDetailEdit(null); };

  // Escape sluit de bovenste modal (snooze/edit/create boven de detail-popup)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (snoozeTicket) setSnoozeTicket(null);
      else if (editingTicket) setEditingTicket(null);
      else if (showCreate) closeCreate();
      else if (detailTicket) { setDetailTicket(null); setDetailEdit(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snoozeTicket, editingTicket, showCreate, detailTicket]);

  const doAction = async (id, action, body) => {
    // Per-ticket busy-guard: geen dubbele acties op hetzelfde ticket
    if (busyIds.has(id)) return false;
    setBusyIds(prev => new Set(prev).add(id));
    try {
      const data = await api(`/api/v1/tickets/${id}/${action}`, { method: 'POST', body: JSON.stringify(body || {}) });
      // Detail-panel open voor dit ticket? → ticketdata + actielog verversen.
      // Serverstatus/toewijzing wordt in de edit-kopie gesynct, tekstuele edits blijven staan.
      if (data && detailTicket && detailTicket.id === id) {
        setDetailTicket(data);
        setDetailEdit(prev => prev ? {
          ...prev,
          status: data.status || prev.status,
          assigned_to_id: data.assigned_to_id ? String(data.assigned_to_id) : '',
        } : prev);
        fetchActions(id);
      }
      refreshAll();
      return data || true;
    } catch (e) { alert('Actie mislukt: ' + e.message); return false; }
    finally {
      setBusyIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  // Opgelost-knop: bij een OPEN gekoppelde compliance-case eerst vragen of
  // de case mee afgesloten moet worden (close_case true/false naar de backend).
  const resolveTicket = (t) => {
    const caseOpen = t.compliance_case_id && !['resolved', 'closed'].includes(t.compliance_case_status);
    if (caseOpen) {
      const alsoCase = window.confirm(`Dit ticket hoort bij compliance-case '${t.compliance_case_title || ''}'. Ook de case afsluiten?`);
      return doAction(t.id, 'resolve', { close_case: alsoCase });
    }
    return doAction(t.id, 'resolve');
  };

  const addAction = async () => {
    const content = newAction.trim();
    if (!content || !detailTicket || postingAction) return;
    setPostingAction(true);
    try {
      const item = await api(`/api/v1/tickets/${detailTicket.id}/actions`, {
        method: 'POST', body: JSON.stringify({ content }),
      });
      setActions(prev => [item, ...prev]);
      setNewAction('');
      setDetailTicket(prev => prev ? { ...prev, action_count: (prev.action_count || 0) + 1 } : prev);
      fetchTickets();
    } catch (e) { alert('Toevoegen mislukt: ' + e.message); }
    finally { setPostingAction(false); }
  };

  const deleteAction = async (actionId) => {
    if (!detailTicket) return;
    if (!window.confirm('Deze opmerking verwijderen?')) return;
    try {
      await api(`/api/v1/tickets/${detailTicket.id}/actions/${actionId}`, { method: 'DELETE' });
      setActions(prev => prev.filter(a => a.id !== actionId));
      setDetailTicket(prev => prev ? { ...prev, action_count: Math.max(0, (prev.action_count || 1) - 1) } : prev);
      fetchTickets();
    } catch (e) { alert('Verwijderen mislukt: ' + e.message); }
  };

  // Eén plek die de create-modal sluit én alle create-state reset
  // (gebruikt door Annuleer, Escape en na succesvol aanmaken).
  const closeCreate = () => {
    setShowCreate(false);
    setForm(EMPTY_FORM);
    setClientSearch('');
    setClientResults([]);
  };

  const createTicket = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const body = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        category: form.category,
      };
      if (form.broker) body.broker = form.broker;
      if (form.related_lead_id) body.related_lead_id = form.related_lead_id;
      // Alleen meesturen bij expliciete keuze — anders geldt de backend-default
      // (backoffice/admin → eigen box, andere rollen → algemene box).
      if (form.assigned_to === 'general') body.assigned_to_id = null;
      else if (form.assigned_to) body.assigned_to_id = Number(form.assigned_to);
      await api('/api/v1/tickets/', { method: 'POST', body: JSON.stringify(body) });
      closeCreate();
      refreshAll();
    } catch (e) { alert('Aanmaken mislukt: ' + e.message); }
    finally { setSaving(false); }
  };

  // Klant zoeken (alle fases) voor koppeling aan een nieuw ticket.
  // 250ms debounce + volgorde-guard: alleen het laatste resultaat wordt geaccepteerd.
  const clientSearchTimer = useRef(null);
  const clientSearchSeq = useRef(0);
  const searchClients = (q) => {
    setClientSearch(q);
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    if (!q || q.trim().length < 2) {
      clientSearchSeq.current += 1; // lopende requests ongeldig maken
      setClientResults([]);
      return;
    }
    clientSearchTimer.current = setTimeout(async () => {
      const seq = ++clientSearchSeq.current;
      try {
        const data = await api(`/api/v1/leads/?search=${encodeURIComponent(q)}&all_stages=true&page_size=8`);
        if (seq === clientSearchSeq.current) setClientResults(data?.leads || []);
      } catch (e) {
        if (seq === clientSearchSeq.current) setClientResults([]);
      }
    }, 250);
  };

  const openSnooze = (ticket) => {
    setSnoozeTicket(ticket);
    setSnoozeForm({ until: addDaysISO(1), note: '' });
  };

  const submitSnooze = async () => {
    if (!snoozeForm.until) return;
    setSaving(true);
    const ok = await doAction(snoozeTicket.id, 'snooze', { until: snoozeForm.until, note: snoozeForm.note || null });
    setSaving(false);
    if (ok) setSnoozeTicket(null);
  };

  const openEdit = (ticket) => {
    setEditingTicket(ticket);
    setEditForm({
      title: ticket.title || '',
      description: ticket.description || '',
      priority: ticket.priority || 'normal',
      category: ticket.category || 'other',
      broker: ticket.broker || '',
      assigned_to_id: ticket.assigned_to_id ? String(ticket.assigned_to_id) : '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const data = await api(`/api/v1/tickets/${editingTicket.id}`, { method: 'PUT', body: JSON.stringify({
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        category: editForm.category,
        broker: editForm.broker || null,
        assigned_to_id: editForm.assigned_to_id ? Number(editForm.assigned_to_id) : null,
      }) });
      if (data && detailTicket && detailTicket.id === editingTicket.id) {
        setDetailTicket(data);
        fetchActions(editingTicket.id);
      }
      setEditingTicket(null);
      refreshAll();
    } catch (e) { alert('Bijwerken mislukt: ' + e.message); }
    finally { setSaving(false); }
  };

  // Opslaan vanuit het detail-panel: PUT alle bewerkbare velden.
  // Statusovergangen: 'snoozed' loopt altijd via de snooze-modal (niet via save),
  // 'resolved' gaat mee in de PUT tenzij er een open compliance-case hangt →
  // dan het resolve-endpoint met close_case (bestaande confirm), 'open' vanuit
  // snoozed/resolved via het reopen-endpoint (zet ook is_woken correct).
  const saveDetail = async () => {
    if (!detailTicket || !detailEdit || !detailEdit.title.trim()) return;
    setSaving(true);
    try {
      const t = detailTicket;
      const body = {
        title: detailEdit.title,
        description: detailEdit.description,
        priority: detailEdit.priority,
        category: detailEdit.category,
        broker: detailEdit.broker || null,
        assigned_to_id: detailEdit.assigned_to_id ? Number(detailEdit.assigned_to_id) : null,
      };
      const statusChanged = detailEdit.status !== t.status;
      if (statusChanged && detailEdit.status === 'resolved') {
        const caseOpen = t.compliance_case_id && !['resolved', 'closed'].includes(t.compliance_case_status);
        if (caseOpen) {
          const alsoCase = window.confirm(`Dit ticket hoort bij compliance-case '${t.compliance_case_title || ''}'. Ook de case afsluiten?`);
          await api(`/api/v1/tickets/${t.id}/resolve`, { method: 'POST', body: JSON.stringify({ close_case: alsoCase }) });
        } else {
          body.status = 'resolved';
        }
      } else if (statusChanged && detailEdit.status === 'open') {
        await api(`/api/v1/tickets/${t.id}/reopen`, { method: 'POST', body: JSON.stringify({}) });
      }
      const data = await api(`/api/v1/tickets/${t.id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (data) {
        setDetailTicket(data);
        setDetailEdit(ticketToEdit(data));
        fetchActions(t.id);
      }
      refreshAll();
    } catch (e) { alert('Opslaan mislukt: ' + e.message); }
    finally { setSaving(false); }
  };

  const deleteTicket = async (id) => {
    if (!window.confirm('Ticket definitief verwijderen?')) return;
    try {
      await api(`/api/v1/tickets/${id}`, { method: 'DELETE' });
      setEditingTicket(null);
      if (detailTicket && detailTicket.id === id) closeDetail();
      refreshAll();
    } catch (e) { alert('Verwijderen mislukt: ' + e.message); }
  };

  // Dropdown-opties voor toewijzing: alle actieve users met ticketrol
  // (uit /tickets/assignees; huidige gebruiker zit in de lijst, label "(ik)").
  const assignOptions = (assignees || [])
    .filter(u => u.id)
    .map(u => u.id === user?.id ? { ...u, full_name: `${u.full_name} (ik)` } : u);

  // Detail/edit-dropdown: staat de huidige assignee niet (meer) in de lijst
  // (bijv. inactief), voeg die als "(inactief)"-optie toe zodat de select niet leeg rendert.
  const assignOptionsFor = (currentId, currentName) => {
    const opts = [...assignOptions];
    const idNum = currentId ? Number(currentId) : null;
    if (idNum && !opts.some(u => u.id === idNum)) {
      opts.push({ id: idNum, full_name: `${currentName || `Gebruiker #${idNum}`} (inactief)` });
    }
    return opts;
  };

  const firstName = (name) => (name || '').split(' ')[0];

  const TAB_DEFS = [
    { key: 'general', label: `Algemeen (${tabs.general_count})`, icon: Inbox },
    { key: 'mine', label: `Mijn tickets (${tabs.my_count})`, icon: UserIcon },
    ...(tabs.users || []).map(u => ({
      key: `user:${u.id}`,
      label: `${firstName(u.full_name)}${u.is_active === false ? ' (inactief)' : ''} (${u.open_count})`,
      icon: UserIcon,
    })),
    { key: 'snoozed', label: `Uitgezet (${tabs.snoozed_count})`, icon: AlarmClock },
    { key: 'resolved', label: 'Opgelost', icon: CheckCircle2 },
  ];

  const showStatusChips = activeTab === 'mine' || activeTab.startsWith('user:');

  const actionBtn = 'flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors whitespace-nowrap';

  // e.stopPropagation() overal: de knoppen staan op een klikbare kaart
  // (detail-popup) en worden ook binnen de popup zelf hergebruikt.
  // Finance is read-only → geen actieknoppen. Tijdens een lopende actie op
  // dit ticket zijn de knoppen disabled (busy-guard).
  const renderActions = (t) => {
    if (isFinance) return [];
    const mine = t.assigned_to_id === user?.id;
    const busy = busyIds.has(t.id);
    const btns = [];
    if (t.status === 'resolved') {
      btns.push(
        <button key="reopen" disabled={busy} onClick={(e) => { e.stopPropagation(); doAction(t.id, 'reopen'); }}
          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#3d61a4', backgroundColor: '#eef2fa' }}>
          <RotateCcw size={12} /> Heropenen
        </button>
      );
    } else if (t.status === 'snoozed') {
      btns.push(
        <button key="wake" disabled={busy} onClick={(e) => { e.stopPropagation(); doAction(t.id, 'reopen'); }}
          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#d97706', backgroundColor: '#fffbeb' }}>
          <AlarmClock size={12} /> Wakker maken
        </button>
      );
      if (mine) {
        btns.push(
          <button key="resolve" disabled={busy} onClick={(e) => { e.stopPropagation(); resolveTicket(t); }}
            className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#16a34a', backgroundColor: '#f0fdf4' }}>
            <Check size={12} /> Opgelost
          </button>
        );
      }
    } else if (mine) {
      btns.push(
        <button key="release" disabled={busy} onClick={(e) => { e.stopPropagation(); doAction(t.id, 'release'); }}
          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#566079', backgroundColor: '#f7f8fc' }}>
          <Undo2 size={12} /> Terug naar algemeen
        </button>,
        <button key="snooze" disabled={busy} onClick={(e) => { e.stopPropagation(); openSnooze(t); }}
          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#d97706', backgroundColor: '#fffbeb' }}>
          <AlarmClock size={12} /> Uitzetten…
        </button>,
        <button key="resolve" disabled={busy} onClick={(e) => { e.stopPropagation(); resolveTicket(t); }}
          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#16a34a', backgroundColor: '#f0fdf4' }}>
          <Check size={12} /> Opgelost ✓
        </button>
      );
    } else {
      btns.push(
        <button key="claim" disabled={busy} onClick={(e) => { e.stopPropagation(); doAction(t.id, 'claim'); }}
          className={`${actionBtn} text-white disabled:opacity-50`} style={{ borderColor: '#3d61a4', backgroundColor: '#3d61a4' }}>
          <Hand size={12} /> Oppakken
        </button>
      );
    }
    return btns;
  };

  return (
    <div className="flex flex-col h-full bg-[#f7f8fc] p-6 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#011745' }}>Tickets</h1>
          <p className="text-sm mt-0.5" style={{ color: '#7b859e' }}>Interne tickets en taken</p>
        </div>
        <div className="flex gap-2">
          <button onClick={refreshAll} className="p-2 rounded-lg border border-[#e8eaf2] hover:bg-white" title="Verversen">
            <RefreshCw size={16} style={{ color: '#7b859e' }} />
          </button>
          {!isFinance && (
            <button onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold"
              style={{ backgroundColor: '#3d61a4' }}>
              <Plus size={16} /> Nieuw ticket
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 flex-wrap border-b border-[#e8eaf2] pb-2">
        {TAB_DEFS.map(({ key, label, icon: Icon }) => {
          const active = activeTab === key;
          return (
            <button key={key} onClick={() => switchTab(key)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
              style={active
                ? { backgroundColor: '#011745', color: 'white' }
                : { backgroundColor: 'white', color: '#566079', border: '1px solid #e8eaf2' }}>
              <Icon size={13} /> {label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {showStatusChips && (
          <div className="flex items-center gap-1.5">
            {[['', 'Alles'], ['open', 'Open'], ['snoozed', 'Uitgezet']].map(([val, label]) => (
              <button key={val || 'all'} onClick={() => setFilterStatus(val)}
                className="text-xs px-2.5 py-1 rounded-full font-medium border transition-colors"
                style={filterStatus === val
                  ? { backgroundColor: '#3d61a4', color: 'white', borderColor: '#3d61a4' }
                  : { backgroundColor: 'white', color: '#566079', borderColor: '#e8eaf2' }}>
                {label}
              </button>
            ))}
          </div>
        )}
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
          className="text-xs border border-[#e8eaf2] rounded-lg px-2 py-1.5 focus:outline-none bg-white"
          style={{ color: '#566079' }}>
          <option value=''>Alle prioriteiten</option>
          {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Ticket list */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-8 h-8 border-2 border-[#3d61a4]/30 border-t-[#3d61a4] rounded-full animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12">
            <Ticket size={40} className="mx-auto mb-3" style={{ color: '#e8eaf2' }} />
            <p className="text-sm font-medium" style={{ color: '#566079' }}>
              {activeTab === 'general' ? 'Geen tickets in de algemene box' : 'Geen tickets gevonden'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {tickets.map(t => {
              const s = STATUSES[t.status] || STATUSES.open;
              const p = PRIORITIES[t.priority] || PRIORITIES.normal;
              const db_ = daysBadge(t.days_open || 0);
              const canEdit = !isFinance && (isAdmin || t.created_by_id === user?.id || t.assigned_to_id === user?.id);
              const woken = t.is_woken && t.status === 'open';
              return (
                <div key={t.id} onClick={() => openDetail(t)}
                  className="bg-white rounded-xl border p-4 hover:shadow-sm transition-all cursor-pointer"
                  style={{ borderColor: woken ? '#d97706' : '#e8eaf2' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold" style={{ color: '#a4abbe' }}>#{t.id}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                        <span className="text-xs font-semibold" style={{ color: p.color }}>{p.label}</span>
                        {t.category && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#f3f4f8]" style={{ color: '#566079' }}>{t.category}</span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: db_.bg, color: db_.color }}>
                          {t.days_open ?? 0} dgn open
                        </span>
                        {woken && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#d97706', color: 'white' }}>
                            ⏰ Terug van uitgezet
                          </span>
                        )}
                        {(t.action_count || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
                            style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                            <MessageSquare size={11} /> {t.action_count}
                          </span>
                        )}
                      </div>
                      <p className="font-semibold text-sm" style={{ color: '#011745' }}>{t.title}</p>
                      {t.related_lead_name && (
                        <span className="inline-flex items-center gap-1 text-xs mt-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                          <Building2 size={11} /> {t.related_lead_name}
                        </span>
                      )}
                      {t.status === 'snoozed' && (
                        <p className="text-xs mt-1 font-medium" style={{ color: '#d97706' }}>
                          <AlarmClock size={11} className="inline mr-1" />
                          Uitgezet tot {dateNL(t.snoozed_until)}{t.snooze_note ? ` — ${t.snooze_note}` : ''}
                        </p>
                      )}
                      {t.description && (
                        <p className="text-xs mt-1 line-clamp-1" style={{ color: '#7b859e' }}>{t.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2">
                        {t.created_by_name && (
                          <span className="text-[11px]" style={{ color: '#a4abbe' }}>Door: {t.created_by_name}</span>
                        )}
                        <span className="text-[11px]" style={{ color: '#3d61a4' }}>
                          → {t.assigned_to_name || 'Algemene box'}
                        </span>
                        {t.broker && (
                          <span className="text-[11px]" style={{ color: '#566079' }}>Broker: {t.broker}</span>
                        )}
                        <span className="text-[11px]" style={{ color: '#a4abbe' }}>
                          <Clock size={10} className="inline mr-0.5" />{dateNL(t.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      {renderActions(t)}
                      {canEdit && (
                        <button onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                          className="p-1.5 rounded-lg hover:bg-[#eef2fa] transition-colors" title="Bewerken">
                          <Edit3 size={14} style={{ color: '#3d61a4' }} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {total > tickets.length && (
              <p className="text-xs text-center py-2" style={{ color: '#7b859e' }}>
                Er zijn meer tickets ({tickets.length} van {total} getoond) — verfijn de filters om de rest te zien.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ─── DETAIL SLIDE-IN PANEL (z-40: snooze/edit-modals komen er met z-50 bovenop) ─── */}
      {detailTicket && detailEdit && (() => {
        const t = detailTicket;
        const s = STATUSES[t.status] || STATUSES.open;
        const db_ = daysBadge(t.days_open || 0);
        const mine = t.assigned_to_id === user?.id;
        const canDelete = !isFinance && (isAdmin || t.created_by_id === user?.id);
        const woken = t.is_woken && t.status === 'open';
        return (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/30 z-40" onClick={closeDetail} />
            {/* Panel */}
            <div className="fixed right-0 top-0 h-full w-[560px] max-w-full bg-white shadow-2xl z-40 flex flex-col overflow-hidden"
              style={{ borderLeft: '1px solid #e8eaf2' }}>

              {/* Header */}
              <div className="px-6 pt-4 pb-0 border-b border-[#e8eaf2]" style={{ backgroundColor: '#f7f8fc' }}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase font-semibold mb-0.5" style={{ color: '#a4abbe' }}>
                      {t.related_lead_name || t.category || 'Ticket'}
                    </p>
                    <h3 className="text-base font-bold truncate" style={{ color: '#011745' }}>{t.title}</h3>
                  </div>
                  <button onClick={closeDetail}
                    className="ml-3 p-2 rounded-lg hover:bg-[#e8eaf2] transition-colors flex-shrink-0">
                    <X size={18} style={{ color: '#566079' }} />
                  </button>
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="text-xs font-bold" style={{ color: '#a4abbe' }}>#{t.id}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
                    {s.label}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: db_.bg, color: db_.color }}>
                    {t.days_open ?? 0} dgn open
                  </span>
                  {woken && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#d97706', color: 'white' }}>
                      ⏰ Terug van uitgezet
                    </span>
                  )}
                  {t.compliance_case_id && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                      <ShieldCheck size={12} /> {t.compliance_case_title || 'Compliance-case'}
                      {t.compliance_case_status && !['resolved', 'closed'].includes(t.compliance_case_status) && (
                        <span className="font-bold">· open</span>
                      )}
                    </span>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1">
                  {[
                    { key: 'details', label: 'Details' },
                    { key: 'actions', label: `Actielog (${t.action_count ?? actions.length})` },
                  ].map(tab => (
                    <button key={tab.key}
                      onClick={() => setDetailTab(tab.key)}
                      className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-all border-b-2 ${
                        detailTab === tab.key
                          ? 'border-[#3d61a4] text-[#3d61a4] bg-white'
                          : 'border-transparent text-[#7b859e] hover:text-[#566079]'
                      }`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Tab: Details ── */}
              {detailTab === 'details' && (
                <>
                  <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Titel */}
                    <div>
                      <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Titel</label>
                      <input type="text" value={detailEdit.title} disabled={isFinance}
                        onChange={e => setDetailEdit(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                        style={{ color: '#011745' }} />
                    </div>

                    {/* Omschrijving */}
                    <div>
                      <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Omschrijving</label>
                      <textarea value={detailEdit.description || ''} disabled={isFinance}
                        onChange={e => setDetailEdit(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4] resize-none"
                        style={{ color: '#011745' }} />
                    </div>

                    {/* Status + Prioriteit */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Status</label>
                        <select value={detailEdit.status} disabled={isFinance}
                          onChange={e => {
                            const v = e.target.value;
                            // "Uitgezet" loopt via de snooze-modal (datum verplicht);
                            // status wordt pas effectief ná bevestigen daar.
                            if (v === 'snoozed' && t.status !== 'snoozed') { openSnooze(t); return; }
                            setDetailEdit(prev => ({ ...prev, status: v }));
                          }}
                          className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                          style={{ color: '#011745' }}>
                          <option value="open">Open</option>
                          <option value="snoozed">Uitgezet</option>
                          <option value="resolved">Opgelost</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Prioriteit</label>
                        <select value={detailEdit.priority} disabled={isFinance}
                          onChange={e => setDetailEdit(prev => ({ ...prev, priority: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                          style={{ color: '#011745' }}>
                          {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Broker / Kanaal */}
                    <div>
                      <label className="text-xs font-semibold block mb-2" style={{ color: '#7b859e' }}>Broker / Kanaal</label>
                      <div style={isFinance ? { pointerEvents: 'none', opacity: 0.6 } : undefined}>
                        <BrokerPicker value={detailEdit.broker}
                          onChange={v => setDetailEdit(prev => ({ ...prev, broker: v }))} />
                      </div>
                      {detailEdit.broker && !BROKERS.some(b => b.key === detailEdit.broker) && (
                        <p className="text-[11px] mt-1.5" style={{ color: '#7b859e' }}>
                          Huidige waarde: "{detailEdit.broker}" — kies een kanaal hierboven of wis.
                        </p>
                      )}
                    </div>

                    {/* Categorie */}
                    <div>
                      <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Categorie</label>
                      <select value={detailEdit.category} disabled={isFinance}
                        onChange={e => setDetailEdit(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                        style={{ color: '#011745' }}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    {/* Toegewezen aan */}
                    <div>
                      <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Toegewezen aan</label>
                      <select value={detailEdit.assigned_to_id} disabled={isFinance}
                        onChange={e => setDetailEdit(prev => ({ ...prev, assigned_to_id: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                        style={{ color: '#011745' }}>
                        <option value=''>Algemene box</option>
                        {assignOptionsFor(detailEdit.assigned_to_id, t.assigned_to_name).map(u => (
                          <option key={u.id} value={u.id}>{u.full_name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Uitgezet-info */}
                    {t.status === 'snoozed' && (
                      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border"
                        style={{ borderColor: '#fde68a', backgroundColor: '#fffbeb' }}>
                        <p className="text-xs font-medium" style={{ color: '#d97706' }}>
                          <AlarmClock size={12} className="inline mr-1" />
                          Uitgezet tot {dateNL(t.snoozed_until)}{t.snooze_note ? ` — ${t.snooze_note}` : ''}
                        </p>
                        {!isFinance && (
                          <button onClick={() => doAction(t.id, 'reopen')} disabled={busyIds.has(t.id)}
                            className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#fde68a', color: '#d97706', backgroundColor: 'white' }}>
                            <AlarmClock size={12} /> Wakker maken
                          </button>
                        )}
                      </div>
                    )}

                    {/* Opgelost-info */}
                    {t.status === 'resolved' && t.resolved_at && (
                      <p className="text-xs font-medium" style={{ color: '#16a34a' }}>
                        <Check size={12} className="inline mr-1" />Opgelost op {dateNL(t.resolved_at)}
                      </p>
                    )}

                    {/* Meta info */}
                    <div className="pt-2 border-t border-[#f3f4f8]">
                      <div className="flex items-center justify-between text-xs" style={{ color: '#a4abbe' }}>
                        <span>Aangemaakt door: <span className="font-medium">{t.created_by_name || '—'}</span></span>
                        <span>{dateNL(t.created_at) || '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer — details tab */}
                  <div className="px-6 py-4 border-t border-[#e8eaf2] flex items-center justify-between gap-3"
                    style={{ backgroundColor: '#f7f8fc' }}>
                    {canDelete ? (
                      <button onClick={() => deleteTicket(t.id)} disabled={saving}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                        <Trash2 size={14} /> Verwijderen
                      </button>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                      {!isFinance && t.status === 'open' && (mine ? (
                        <button onClick={() => doAction(t.id, 'release')} disabled={busyIds.has(t.id)}
                          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#566079', backgroundColor: 'white' }}>
                          <Undo2 size={12} /> Terug naar algemeen
                        </button>
                      ) : (
                        <button onClick={() => doAction(t.id, 'claim')} disabled={busyIds.has(t.id)}
                          className={`${actionBtn} disabled:opacity-50`} style={{ borderColor: '#e8eaf2', color: '#3d61a4', backgroundColor: '#eef2fa' }}>
                          <Hand size={12} /> Oppakken
                        </button>
                      ))}
                      <button onClick={closeDetail}
                        className="px-4 py-2 rounded-lg text-sm font-medium" style={{ color: '#7b859e' }}>
                        {isFinance ? 'Sluiten' : 'Annuleer'}
                      </button>
                      {!isFinance && (
                        <button onClick={saveDetail} disabled={saving || !detailEdit.title.trim()}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                          style={{ backgroundColor: '#3d61a4' }}>
                          {saving
                            ? <span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            : <Save size={14} />}
                          Opslaan
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── Tab: Actielog ── */}
              {detailTab === 'actions' && (
                <div className="flex-1 overflow-y-auto p-6">
                  {!isFinance && (
                    <div className="flex gap-2 mb-3">
                      <input value={newAction} onChange={e => setNewAction(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addAction(); }}
                        maxLength={5000}
                        placeholder="Actie toevoegen… (bijv. document opgevraagd bij klant)"
                        className="flex-1 border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#3d61a4]" />
                      <button onClick={addAction} disabled={postingAction || !newAction.trim()}
                        className="px-3 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                        style={{ backgroundColor: '#3d61a4' }}>
                        {postingAction ? '...' : 'Toevoegen'}
                      </button>
                    </div>
                  )}
                  {actionsLoading ? (
                    <div className="flex justify-center py-4">
                      <div className="w-5 h-5 border-2 border-[#3d61a4]/30 border-t-[#3d61a4] rounded-full animate-spin" />
                    </div>
                  ) : actions.length === 0 ? (
                    <p className="text-xs py-2" style={{ color: '#a4abbe' }}>Nog geen acties op dit ticket.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {actions.map(a => a.action_type === 'comment' ? (
                        <div key={a.id} className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm whitespace-pre-wrap" style={{ color: '#252f4a' }}>{a.content}</p>
                            <p className="text-[11px] mt-0.5" style={{ color: '#a4abbe' }}>
                              {a.user_name || 'Onbekend'} · {relTime(a.created_at)}
                            </p>
                          </div>
                          {!isFinance && (isAdmin || a.user_id === user?.id) && (
                            <button onClick={() => deleteAction(a.id)} title="Opmerking verwijderen"
                              className="p-1 rounded hover:bg-red-50 flex-shrink-0">
                              <X size={12} style={{ color: '#dc2626' }} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div key={a.id} className="flex items-center gap-1.5">
                          {a.action_type === 'assign'
                            ? <UserIcon size={11} style={{ color: '#a4abbe' }} className="flex-shrink-0" />
                            : <Clock size={11} style={{ color: '#a4abbe' }} className="flex-shrink-0" />}
                          <p className="text-xs italic" style={{ color: '#7b859e' }}>
                            {a.content} — {a.user_name || 'Systeem'} · {relTime(a.created_at)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* ─── SNOOZE MODAL ─── */}
      {snoozeTicket && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold flex items-center gap-2" style={{ color: '#011745' }}>
                <AlarmClock size={16} style={{ color: '#d97706' }} /> Ticket #{snoozeTicket.id} uitzetten
              </h2>
              <button onClick={() => setSnoozeTicket(null)} className="p-1.5 rounded-lg hover:bg-[#f3f4f8]">
                <X size={18} style={{ color: '#7b859e' }} />
              </button>
            </div>
            <p className="text-sm mb-3" style={{ color: '#566079' }}>
              Het ticket verdwijnt naar "Uitgezet" en komt op de gekozen datum automatisch bovenaan terug.
            </p>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {SNOOZE_PRESETS.map(preset => {
                const v = preset.value();
                return (
                  <button key={preset.label} onClick={() => setSnoozeForm(prev => ({ ...prev, until: v }))}
                    className="text-xs px-2.5 py-1.5 rounded-lg border font-medium transition-colors"
                    style={snoozeForm.until === v
                      ? { backgroundColor: '#d97706', color: 'white', borderColor: '#d97706' }
                      : { backgroundColor: '#f7f8fc', color: '#566079', borderColor: '#e8eaf2' }}>
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Tot en met</label>
                <input type="date" value={snoozeForm.until} min={addDaysISO(1)}
                  onChange={e => setSnoozeForm(prev => ({ ...prev, until: e.target.value }))}
                  className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d97706]" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Notitie (optioneel)</label>
                <textarea value={snoozeForm.note} rows={2} placeholder="Waarom wordt dit ticket uitgezet?"
                  onChange={e => setSnoozeForm(prev => ({ ...prev, note: e.target.value }))}
                  className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d97706] resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={submitSnooze} disabled={saving || !snoozeForm.until}
                className="flex-1 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#d97706' }}>{saving ? 'Uitzetten...' : 'Uitzetten'}</button>
              <button onClick={() => setSnoozeTicket(null)}
                className="px-4 py-2 rounded-lg text-sm border border-[#e8eaf2]">Annuleren</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── EDIT MODAL ─── */}
      {editingTicket && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e8eaf2]">
              <h2 className="text-base font-bold" style={{ color: '#011745' }}>
                Ticket #{editingTicket.id} bewerken
              </h2>
              <button onClick={() => setEditingTicket(null)} className="p-1.5 rounded-lg hover:bg-[#f3f4f8]">
                <X size={18} style={{ color: '#7b859e' }} />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Titel *</label>
                <input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d61a4]" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Beschrijving</label>
                <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  rows={3} className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d61a4] resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Prioriteit</label>
                  <select value={editForm.priority} onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none">
                    {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Categorie</label>
                  <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Broker (informatief)</label>
                <input type="text" value={editForm.broker || ''}
                  onChange={e => setEditForm(p => ({ ...p, broker: e.target.value }))}
                  placeholder="Naam van de broker"
                  className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3d61a4]" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block" style={{ color: '#566079' }}>Toegewezen aan</label>
                <select value={editForm.assigned_to_id} onChange={e => setEditForm(p => ({ ...p, assigned_to_id: e.target.value }))}
                  className="w-full border border-[#e8eaf2] rounded-lg px-3 py-2 text-sm focus:outline-none">
                  <option value=''>Algemene box</option>
                  {assignOptionsFor(editForm.assigned_to_id, editingTicket.assigned_to_name).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#e8eaf2]">
              {(isAdmin || editingTicket.created_by_id === user?.id) ? (
                <button onClick={() => deleteTicket(editingTicket.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 size={14} /> Verwijderen
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button onClick={() => setEditingTicket(null)}
                  className="px-4 py-2 rounded-lg text-sm border border-[#e8eaf2]">Annuleren</button>
                <button onClick={saveEdit} disabled={saving || !editForm.title?.trim()}
                  className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                  style={{ backgroundColor: '#3d61a4' }}>{saving ? 'Opslaan...' : 'Opslaan'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── CREATE MODAL ─── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header — zelfde stijl als de detailview */}
            <div className="px-6 py-4 border-b border-[#e8eaf2]" style={{ backgroundColor: '#f7f8fc' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#a4abbe' }}>Ticketsysteem</p>
              <h2 className="text-lg font-bold mt-0.5" style={{ color: '#011745' }}>Nieuw ticket</h2>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Titel *</label>
                <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                  style={{ color: '#011745' }} />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Omschrijving</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4] resize-none"
                  style={{ color: '#011745' }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Prioriteit</label>
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                    style={{ color: '#011745' }}>
                    {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Categorie</label>
                  <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                    style={{ color: '#011745' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Broker / Kanaal</label>
                <BrokerPicker value={form.broker} onChange={b => setForm(p => ({ ...p, broker: b }))} />
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Klant koppelen (optioneel)</label>
                {form.related_lead_id ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#eef2fa' }}>
                    <span className="text-sm font-medium flex-1" style={{ color: '#011745' }}>{form.related_lead_name}</span>
                    <button onClick={() => setForm(p => ({ ...p, related_lead_id: null, related_lead_name: '' }))}
                      className="text-xs font-bold px-2 py-0.5 rounded hover:bg-white" style={{ color: '#7b859e' }}>✕</button>
                  </div>
                ) : (
                  <div className="relative">
                    <input value={clientSearch} onChange={e => searchClients(e.target.value)}
                      placeholder="Zoek op bedrijfsnaam…"
                      className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                      style={{ color: '#011745' }} />
                    {clientResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-[#e8eaf2] shadow-lg z-10 max-h-44 overflow-y-auto">
                        {clientResults.map(l => (
                          <button key={l.id}
                            onClick={() => { setForm(p => ({ ...p, related_lead_id: l.id, related_lead_name: l.company_name })); setClientSearch(''); setClientResults([]); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#f7f8fc] transition-colors"
                            style={{ color: '#011745' }}>
                            {l.company_name}
                            <span className="text-xs ml-2" style={{ color: '#a4abbe' }}>{l.pipeline_stage}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold block mb-1" style={{ color: '#7b859e' }}>Toewijzen aan</label>
                <select value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#e8eaf2] text-sm focus:outline-none focus:border-[#3d61a4]"
                  style={{ color: '#011745' }}>
                  <option value=''>Standaard (eigen box / algemene box)</option>
                  <option value='general'>Algemene box</option>
                  {assignOptions.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              </div>
            </div>

            {/* Footer — zelfde stijl als de detailview */}
            <div className="px-6 py-4 border-t border-[#e8eaf2] flex items-center justify-end gap-2" style={{ backgroundColor: '#f7f8fc' }}>
              <button onClick={closeCreate}
                className="px-4 py-2 rounded-lg text-sm border border-[#e8eaf2] bg-white" style={{ color: '#566079' }}>Annuleer</button>
              <button onClick={createTicket} disabled={saving || !form.title.trim()}
                className="px-5 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#3d61a4' }}>{saving ? 'Aanmaken…' : '+ Ticket aanmaken'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
